import { createContext, createElement, useContext, useMemo, type ReactNode } from "react";
import { useStore } from "zustand";

import { resolveLocale, DEFAULT_LOCALE, NO_CURRENCY_LOCALE } from "~/i18n/currency";
import { formatMoney } from "~/i18n/format-money";
import { currencyStore, type CurrencyState } from "~/stores/currency-store";

// ---------------------------------------------------------------------------
// Request-scoped currency context
// ---------------------------------------------------------------------------
//
// `currencyStore` (Zustand) is a module-level singleton — safe for the CLIENT
// (one browser tab, one user) but never seeded during SSR to avoid leaking one
// request's currency into another concurrent request on the same Node process.
// That left SSR always rendering money with `currency: null` while the client's
// hydration render seeded the store synchronously with the real value — a
// genuine React text-hydration mismatch on every money value.
//
// Fix: thread the request's currency through the root loader into this React
// Context instead. Context IS request-scoped (a fresh Provider per render tree),
// so server and client render the SAME value on the first pass. The Zustand
// store still exists for later imperative updates (e.g. mid-session currency
// change) — `useActiveCurrency` prefers the context value only until the store
// catches up to it, then defers to the store as the live source of truth.

const CurrencyContext = createContext<string | null>(null);

export const CurrencyProvider = ({ currency, children }: { currency: string | null; children: ReactNode }) => createElement(CurrencyContext.Provider, { value: currency }, children);

// ---------------------------------------------------------------------------
// Imperative actions (no hooks — safe to call from loaders/effects/handlers)
// ---------------------------------------------------------------------------

export const currencyActions = {
  initFromLoader: (currency: string | null) => currencyStore.getState().initFromLoader(currency),
  reset: () => currencyStore.getState().reset(),
};

// ---------------------------------------------------------------------------
// React hook binding for the vanilla currencyStore
// ---------------------------------------------------------------------------

export function useCurrencyStore<T>(selector: (state: CurrencyState) => T): T {
  return useStore(currencyStore, selector);
}

/**
 * Read the operator's active currency and its resolved formatting locale.
 *
 * Returns `{ currency: null, locale: null }` when no currency is configured.
 * The hook does NOT guard — pages decide whether to block (see <RequireCurrency>).
 * Pass the result into `formatMoney(value, { currency, locale })`.
 *
 * Prefers the request-scoped `CurrencyProvider` context (identical on server and
 * client first render) over the Zustand store until the store is seeded with the
 * same value — this is what keeps SSR and hydration output in sync. Once
 * `initFromLoader`/the mid-session re-sync effect runs, the store becomes the
 * live source of truth (context never updates after the initial loader render).
 */
export function useActiveCurrency(): { currency: string | null; locale: string | null } {
  const contextCurrency = useContext(CurrencyContext);
  const storeCurrency = useCurrencyStore((s) => s.currency);
  const currency = storeCurrency ?? contextCurrency;

  return { currency, locale: currency ? resolveLocale(currency) : null };
}

/** Options for the consolidated {@link useMoneyFormatters} `formatMoney`. */
export interface UseFormatMoneyOptions {

  /** Maximum fraction digits. Default 2. */
  decimalRound?: number;

  /** Prepend the locale-correct currency symbol (₹, $, …). Default true. Ignored when no currency is configured. */
  symbol?: boolean;

  /**
   * Sign display. Default = minus on negatives only. Pass "exceptZero" for P&L
   * surfaces that need an explicit "+" on positives — Intl places it locale-correctly.
   */
  signDisplay?: Intl.NumberFormatOptions["signDisplay"];
}

/**
 * Single money formatter pre-bound to the operator's active currency + locale.
 *
 * Replaces the legacy trio (formatAmount / formatNumberWithSeparator /
 * formatCurrency) with one call:
 *
 *   const { formatMoney } = useMoneyFormatters();
 *   formatMoney(value)                  // ₹12,34,567.5  — symbol + operator-currency grouping
 *   formatMoney(value, { symbol: false })// 12,34,567.5   — grouping only, no symbol
 *
 * No-currency behaviour: there is intentionally NO silent INR fallback (the bug
 * this i18n work fixes). When currency is unconfigured the amount is STILL
 * rendered — just symbol-less, in neutral Western grouping (`1,234,567`) — never
 * a placeholder, so totals/values stay readable even before currency is set.
 * Pages that must block the whole region still use <RequireCurrency>.
 */
export function useMoneyFormatters() {
  const { currency, locale } = useActiveCurrency();

  return useMemo(() => {
    // No currency configured → still render the amount, just WITHOUT any symbol.
    // Plain en-US grouping; never an INR symbol, never a "—" placeholder.
    if (!currency) {
      return {
        currency: null as string | null,
        locale: NO_CURRENCY_LOCALE,
        formatMoney: (value: number, options?: UseFormatMoneyOptions): string => formatMoney(value, { currency: "", locale: NO_CURRENCY_LOCALE, decimalRound: options?.decimalRound ?? 2, style: "decimal", signDisplay: options?.signDisplay }) || "0",
      };
    }
    const cur = currency;
    const loc = locale ?? DEFAULT_LOCALE;

    return {
      currency: cur as string | null,
      locale: loc,

      /**
       * Format an amount in the operator's currency. `symbol` (default true)
       * prepends the locale-correct currency symbol; `symbol: false` gives
       * digits-only grouping.
       */
      formatMoney: (value: number, options?: UseFormatMoneyOptions): string =>
        formatMoney(value, {
          currency: cur,
          locale: loc,
          decimalRound: options?.decimalRound ?? 2,
          style: options?.symbol === false ? "decimal" : "currency",
          signDisplay: options?.signDisplay,
        }) || "0",
    };
  }, [currency, locale]);
}
